/**
 * @fileoverview Reactivate User notes.
 * @description What the Reactivate dialog is confirming: the placement the user had when
 * deactivated, and which email they will get (a welcome-back email with a set-new-password link
 * for someone who had activated, or a fresh invitation for someone who never finished activating).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { REACTIVATE_TEST_ID } from '@/features/users/constants/lifecycle.constants'

import type { UserDetail } from '@/features/users/types/users.types'

/**
 * @component
 * @param {{ user: UserDetail }} props - The user coming back.
 * @param {UserDetail} props.user - The user coming back.
 */
function ReactivationNotes({ user }: { user: UserDetail }) {
  const { t } = useTranslation()
  const manager = user.reportingManager?.displayName
  // Someone who never activated has no password to restore: they are invited again instead.
  const neverActivated = user.joinedAt === null

  return (
    <>
      <p
        className="rounded-lg border border-input-border bg-input-bg px-3.5 py-2.5 text-[13px] text-ink"
        data-testid={`${REACTIVATE_TEST_ID}-previous-placement`}
      >
        {manager
          ? t('sales.userManagement.reactivateUser.previousPlacementUnder', {
              name: user.displayName,
              role: user.primaryRole?.name ?? '',
              manager,
            })
          : t('sales.userManagement.reactivateUser.previousPlacement', {
              name: user.displayName,
              role: user.primaryRole?.name ?? '',
            })}
      </p>
      <p
        className="rounded-lg border border-status-warning/40 bg-warning-bg px-3.5 py-2.5 text-[13px] text-ink"
        data-testid={`${REACTIVATE_TEST_ID}-email-note`}
      >
        {t(
          neverActivated
            ? 'sales.userManagement.reactivateUser.invitationEmail'
            : 'sales.userManagement.reactivateUser.welcomeBackEmail',
          { email: user.email },
        )}
      </p>
    </>
  )
}

export default ReactivationNotes
